import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar';
import AnimatedBackground from './ui/AnimatedBackground';
import PageTransition from './ui/PageTransition';
import DemoModeBar from './ui/DemoModeBar';
import CommandPalette from './ui/CommandPalette';
import { useAuth } from '../context/AuthContext';

/**
 * AppLayout — Layout condiviso per tutte le pagine /app/*.
 * La Sidebar è montata UNA SOLA VOLTA e NON viene smontata durante la navigazione.
 * L'Outlet è wrappato in AnimatePresence per animazioni di entrata/uscita fluide.
 * Se l'account è demo, mostra il DemoModeBar in cima.
 * Su mobile la sidebar è collassabile tramite hamburger.
 */
const AppLayout = () => {
    const location = useLocation();
    const { isDemoAccount, impersonating } = useAuth();
    const [demoView, setDemoView] = useState('agency');
    const [demoResetKey, setDemoResetKey] = useState(0);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);

    // Ctrl+K / Cmd+K apre la Command Palette
    useEffect(() => {
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setPaletteOpen(o => !o);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const handleDemoReset = () => setDemoResetKey(k => k + 1);

    // Sidebar mini-mode: collassa le label quando si è su /marketing
    const isMarketing = location.pathname.startsWith('/app/marketing');

    return (
        <div className="flex h-screen relative overflow-hidden">
            <AnimatedBackground variant="light" />

            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar — fixed on mobile, sticky on desktop so it never scrolls */}
            <div className={`
                fixed inset-y-0 left-0 z-50 lg:relative lg:z-auto lg:flex-shrink-0
                lg:h-screen lg:overflow-y-auto
                transition-all duration-300 ease-in-out overflow-hidden
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <Sidebar
                    demoView={demoView}
                    onClose={() => setSidebarOpen(false)}
                    mini={isMarketing}
                />
            </div>

            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {isDemoAccount && (
                    <DemoModeBar
                        view={demoView}
                        onViewChange={setDemoView}
                        onReset={handleDemoReset}
                    />
                )}

                {/* Mobile top bar — fixed so it stays visible when scrolling */}
                <div className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-black/80 backdrop-blur-sm">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Apri menu"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                    <img src="/logo-onet.png" alt="ONET" className="h-7 w-auto" />
                </div>

                <main className={`flex-1 overflow-y-auto lg:pt-0 pt-14 ${impersonating ? 'lg:pt-10' : ''}`}>
                    <AnimatePresence mode="wait" initial={false}>
                        <PageTransition key={location.pathname} variant="rise">
                            <Outlet context={{ demoView, demoResetKey }} />
                        </PageTransition>
                    </AnimatePresence>
                </main>
            </div>

            {/* Command Palette (Ctrl+K) */}
            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
    );
};

export default AppLayout;

